import { If, VLazyLoadMultiSelect, VSelectOptionWithDescription } from '@vision/ui/components';
import { useEmojiContext, useSelectedAccountId } from '@vision/ui/hooks';
import { ReportsDetailGraphicSchemaValues, SimpleFlowQuestion } from '@vision/ui/interfaces';
import {
  useLazyFetchFlowQuestionsWithPaginationQuery,
  useLazyFetchFlowsWithPaginationQuery,
} from '@vision/ui/services';
import { getBodyText, insertIfObject, translateErrorMessage } from '@vision/ui/utils';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildMatrixQuestionStatementOptions,
  hasSelectedQuestionWithStatements as hasSelectedQuestionWithStatementsInData,
  MatrixFlowQuestionItem,
  ReportsDetailGraphicCompareMatrixQuestionStatementsSelect,
  useReportsDetailGraphicCompareMatrixQuestionStatements,
} from '../ReportsDetailGraphicCompareMatrixQuestionStatementsSelect';
import { ReportsDetailGraphicCompareLayout } from '../ReportsDetailGraphicCompareLayout';
import { ReportsDetailGraphicBaseProps } from '../report-detail-graphic-compare.util';

interface ReportsDetailGraphicCompareQuestionKeyProps extends ReportsDetailGraphicBaseProps {}

export function ReportsDetailGraphicCompareQuestionKey({
  compareItemType,
  required,
}: ReportsDetailGraphicCompareQuestionKeyProps) {
  const { t, i18n } = useTranslation();
  const accountId = useSelectedAccountId();
  const formik = useFormikContext<ReportsDetailGraphicSchemaValues>();
  const { textWithNativeEmoji } = useEmojiContext();
  const [flowQuestions, setFlowQuestions] = useState<MatrixFlowQuestionItem[]>([]);

  const selectedQuestionKeyIds = formik.values.selectedCompareCriteria.selectedQuestionKeyIds;

  const handleDefaultValuesLoaded = (data: SimpleFlowQuestion[]) => {
    // data içindeki idleri formik.values.selectedCompareCriteria.selectedQuestionIds içinde varsa onu sil
    const newIds = formik.values.selectedCompareCriteria.selectedQuestionKeyIds.filter((id) =>
      data.some((item) => item.id === id),
    );
    formik.setFieldValue('selectedCompareCriteria.selectedQuestionKeyIds', newIds);
  };

  const mapItemToSelectOption = (item: SimpleFlowQuestion) => ({
    value: item.id,
    label: item.key,
    description: `${textWithNativeEmoji(getBodyText(item.body, i18n.language))}`,
    ...insertIfObject(!!item?.flow_name, {
      title: `${textWithNativeEmoji(getBodyText(item.body, i18n.language))}\n\n${
        item?.flow_name && `${t('flowName')}: ${item?.flow_name}`
      }`,
    }),
  });

  const matrixQuestionStatements = useMemo(() => {
    return buildMatrixQuestionStatementOptions({
      selectedQuestionIds: selectedQuestionKeyIds,
      flowQuestions,
      language: i18n.language,
      textWithNativeEmoji,
    });
  }, [selectedQuestionKeyIds, flowQuestions, textWithNativeEmoji, i18n.language]);

  const hasSelectedQuestionWithStatements = useMemo(() => {
    return hasSelectedQuestionWithStatementsInData(selectedQuestionKeyIds, flowQuestions);
  }, [selectedQuestionKeyIds, flowQuestions]);

  const { selectedMatrixQuestionStatements, shouldShowMatrixQuestionStatements, handleChangeMatrixQuestionStatements } =
    useReportsDetailGraphicCompareMatrixQuestionStatements({
      formik,
      selectedFlowIds: formik.values.selectedCompareCriteria.selectedQuestionKeyFlowIds,
      selectedQuestionIds: selectedQuestionKeyIds,
      hasSelectedQuestionWithStatements,
      matrixQuestionStatements,
    });

  return (
    <ReportsDetailGraphicCompareLayout title={t(`page-reports:reportCompareGroupItemType.${compareItemType}`)}>
      <VLazyLoadMultiSelect
        id="report-compare-question-key-flow"
        data-testid="input-report-compare-question-key-flow"
        label={t('flows')}
        useLazyApiQueryFunction={useLazyFetchFlowsWithPaginationQuery}
        apiRequestParameters={{ nodeId: accountId, questionKeyNotNull: true, sortBy: 'name', sortOrder: 'asc' }}
        multiSelectItemMapper={(items) => {
          return items.map((item) => ({
            value: item.id,
            label: item.name,
          }));
        }}
        searchKey="flow_name"
        value={formik.values.selectedCompareCriteria.selectedQuestionKeyFlowIds}
        searchable={true}
        clearable={true}
        onBlur={formik.handleBlur}
        onChange={(values) => {
          formik.setFieldValue('selectedCompareCriteria.selectedQuestionKeyFlowIds', values);
          formik.setFieldValue('selectedCompareCriteria.selectedQuestionKeyIds', []);
          formik.setFieldValue('selectedCompareCriteria.selectedMatrixQuestionStatementIds', []);
        }}
        error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionKeyFlowIds')}
        withAsterisk={required}
        itemComponent={VSelectOptionWithDescription}
      />

      <If value={formik.values.selectedCompareCriteria.selectedQuestionKeyFlowIds.length > 0}>
        <VLazyLoadMultiSelect
          id="report-compare-flow-question-key"
          data-testid="input-report-compare-flow-question-key"
          label={t('question')}
          useLazyApiQueryFunction={useLazyFetchFlowQuestionsWithPaginationQuery}
          apiRequestParameters={{
            nodeId: accountId,
            flowIds: formik.values.selectedCompareCriteria.selectedQuestionKeyFlowIds,
            keyNotNull: true,
            sortBy: 'name',
            sortOrder: 'asc',
          }}
          multiSelectItemMapper={(items) => items.filter((item) => !!item.key).map(mapItemToSelectOption)}
          searchKey="key"
          searchable={true}
          value={selectedQuestionKeyIds}
          onBlur={formik.handleBlur}
          onChange={(e) => {
            formik.setFieldValue('selectedCompareCriteria.selectedQuestionKeyIds', e);
            formik.setFieldValue('selectedCompareCriteria.selectedMatrixQuestionStatementIds', []);
          }}
          error={translateErrorMessage(formik, 'selectedCompareCriteria.selectedQuestionKeyIds')}
          resetChangeRequestParameters={true}
          onDefaultValuesLoaded={handleDefaultValuesLoaded}
          onDataChange={(data: SimpleFlowQuestion[]) => setFlowQuestions(data as MatrixFlowQuestionItem[])}
          itemComponent={VSelectOptionWithDescription}
        />

        <If value={shouldShowMatrixQuestionStatements}>
          <ReportsDetailGraphicCompareMatrixQuestionStatementsSelect
            id="report-compare-question-key-matris-question-statements"
            dataTestId="input-report-compare-question-key-matris-question-statements"
            data={matrixQuestionStatements}
            value={selectedMatrixQuestionStatements}
            onBlur={formik.handleBlur}
            onChange={handleChangeMatrixQuestionStatements}
          />
        </If>
      </If>
    </ReportsDetailGraphicCompareLayout>
  );
}
